import React from "react";
import { formatMessage } from "lib/globalize-util";
import {
    DirectionalHint,
    nuOrderTheme as theme,
    IconButton,
    IContextualMenuItem,
    IContextualMenuProps,
} from "@nuorder/components";
import { $, merge } from "glamor";
import classNames from "classnames";
import { DATATEST } from "constants/integration";
import { useFlags } from "launchdarkly-react-client-sdk";
import { MAX_USER_CUSTOM_ATTRIBUTES } from "constants/product-collection";
import { shouldDisableAddGrouping } from "../../utils";
import { Attribute } from "reducers/dimensions-panel/dimensions-panel";
import { DimentionActionCommands } from "../../dimension-types";

interface IProps {
    attributes: Attribute[];
    editable: boolean;
    handleAddDoorGrouping: () => void;
    handleImportAttributesToggle: () => void;
    handleEditDoorGrouping: () => void;
    customAttributesLimit: number;
}

const styles = merge(
    $(".toolbar", {
        display: "flex",
        width: "100%",
        justifyContent: "flex-end",
        padding: "0 10px",
    }),
    $(" .item svg", {
        fill: theme.palette.greyDarker,
    }),
    $(" .item:disabled", {
        background: "none",
    })
);

export const DoorAttributesToolbar: React.FunctionComponent<IProps> = ({
    attributes,
    editable,
    handleAddDoorGrouping,
    handleImportAttributesToggle,
    handleEditDoorGrouping,
    customAttributesLimit,
}) => {
    const handleMenuClick = React.useCallback(
        (_ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, item?: IContextualMenuItem) => {
            switch (item?.key) {
                case DimentionActionCommands.AddGrouping:
                    handleAddDoorGrouping();
                    break;
                case DimentionActionCommands.ImportDoorGroupings:
                    handleImportAttributesToggle();
                    break;
                default:
                    break;
            }
        },
        [handleAddDoorGrouping, handleImportAttributesToggle]
    );
    const numberOfCustomOptions = attributes.filter((attribute) => attribute.isUserDefined).length;

    const { importDoorAttributes } = useFlags();

    const items: IContextualMenuItem[] = [
        {
            key: DimentionActionCommands.AddGrouping,
            text: formatMessage("Add manually"),
            disabled: shouldDisableAddGrouping(
                numberOfCustomOptions,
                customAttributesLimit,
                MAX_USER_CUSTOM_ATTRIBUTES
            ),
            "data-test": DATATEST.ASSORTMENTS.GROUPING.ATTRIBUTES.OPEN_ADD_ATTRIBUTE_DIALOG,
        },
    ];
    if (importDoorAttributes) {
        items.push({
            key: DimentionActionCommands.ImportDoorGroupings,
            text: formatMessage("Import door groupings"),
            "data-test": DATATEST.ASSORTMENTS.GROUPING.ATTRIBUTES.OPEN_IMPORT_DOOR_GROUPINGS_DIALOG,
        });
    }

    const addProps: IContextualMenuProps = {
        directionalHint: DirectionalHint.leftCenter,
        gapSpace: 0,
        beakWidth: 20,
        directionalHintFixed: false,
        items,
        onItemClick: handleMenuClick,
    };

    return (
        <>
            <div {...styles} className={classNames("toolbar", "toolbarContainer")}>
                {editable && (
                    <IconButton
                        role="button"
                        className="item"
                        aria-haspopup
                        title={formatMessage("Add grouping")}
                        menuIconProps={{
                            iconName: "plus24",
                        }}
                        data-test={DATATEST.ASSORTMENTS.GROUPING.ATTRIBUTES.OPEN_ADD_ATTRIBUTE_ITEM}
                        menuProps={addProps}
                    />
                )}

                <IconButton
                    role="button"
                    className="item"
                    title={formatMessage((!editable ? "View" : "Edit") + " door groupings")}
                    onClick={handleEditDoorGrouping}
                    menuIconProps={{
                        iconName: !editable ? "horizKabob24" : "pencil24",
                    }}
                    data-test={DATATEST.ASSORTMENTS.GROUPING.ATTRIBUTES.EDIT_DOOR_GROUPINGS}
                />
            </div>
        </>
    );
};
